import { ApiService, ApiServiceType } from "../../../ApiService";

export interface PrScreenStatus {
  acquired: boolean;
  canRecover: boolean;
  canRelease: boolean;
  message: string;
}

export function newPrScreen(): string {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, value => value.toString(16).padStart(2, "0")).join("");
}

export class PrScreenSession {
  screen = newPrScreen();
  acquired = false;
  canRecover = false;
  canRelease = false;
  message = "";
  private folio = 0;
  private revision = 0;
  private client() {
    return new ApiService("Sales/PrSession", ApiServiceType.TenantDatabase).withPrScreen(this.screen);
  }

  async acquire(folio: number, recover = false): Promise<boolean> {
    if (this.folio && this.folio !== folio) {
      await this.release();
    }
    this.folio = folio;
    ++this.revision;
    this.acquired = false;
    this.canRecover = false;
    this.canRelease = false;
    if (recover) this.screen = newPrScreen();
    try {
      const result: PrScreenStatus = await this.client().post(null, `${recover ? "Recover" : "Acquire"}/${folio}`);
      this.acquired = result.acquired;
      this.canRecover = result.canRecover;
      this.canRelease = result.canRelease;
      this.message = result.message;
      return result.acquired;
    } catch (error) {
      this.message = String(error);
      return false;
    }
  }

  async check(): Promise<boolean> {
    if (!this.folio) return false;
    const revision = ++this.revision;
    try {
      const result: PrScreenStatus = await this.client().post(null, `Check/${this.folio}`);
      if (revision !== this.revision) return this.acquired;
      this.acquired = result.acquired;
      this.canRecover = result.canRecover;
      this.canRelease = result.canRelease;
      this.message = result.message;
    } catch (error) {
      if (revision !== this.revision) return this.acquired;
      this.acquired = false;
      this.canRecover = false;
      this.canRelease = false;
      this.message = String(error);
    }
    return this.acquired;
  }

  async recover(): Promise<boolean> {
    if (!this.canRecover || !window.confirm("¿Recuperar este PR aquí? Se recargarán los datos guardados. Los cambios locales sin guardar se descartarán y la pantalla anterior de tu cuenta perderá el control.")) {
      return false;
    }
    return this.acquire(this.folio, true);
  }

  async free(): Promise<boolean> {
    if (!this.folio || !this.canRelease || !window.confirm("¿Liberar este PR? Quedará disponible para WEB o NET. La pantalla anterior perderá el control y los cambios sin guardar deberán capturarse de nuevo.")) {
      return false;
    }
    ++this.revision;
    this.acquired = false;
    this.canRecover = false;
    this.canRelease = false;
    try {
      const result: PrScreenStatus = await this.client().post(null, `Free/${this.folio}`);
      this.screen = newPrScreen();
      this.canRecover = result.canRecover;
      this.canRelease = result.canRelease;
      this.message = result.message;
      return true;
    } catch (error) {
      this.message = String(error);
      return false;
    }
  }

  async release(): Promise<void> {
    if (!this.folio || !this.acquired) {
      return;
    }
    ++this.revision;
    this.acquired = false;
    try {
      await this.client().post(null, `Release/${this.folio}`);
    } catch {
      // Never force-delete or release a successor's/active operation.
    }
  }
}
